/**
 * Order Totals card labels: shipping method / carrier and special fee names.
 */

function str(v: unknown): string {
  return String(v ?? "").trim();
}

function isTruthyPickup(v: unknown): boolean {
  return v === true || v === 1 || String(v).toLowerCase() === "true";
}

/** Same heuristic as SignageEditOrder: UPS service codes are short alphanumeric ids. */
function looksLikeUpsServiceId(rawId: unknown): boolean {
  const id = str(rawId);
  if (!id || id === "custom-manual") return false;
  return /^[0-9A-Z]{2,3}$/.test(id) && id.length <= 3;
}

/**
 * Full left-column label for shipping line, e.g. `Shipping Charge: (UPS: Next Day Air)` or
 * `Shipping Charge: (Customer pickup)` / `Shipping Charge: (Flat Rate)`.
 */
export function formatShippingChargeOrderTotalsLabel(
  rows: unknown[] | null | undefined,
  opts?: { isUps?: boolean }
): string {
  const arr = Array.isArray(rows) ? rows : [];
  const row = (arr[0] as Record<string, unknown>) || null;
  if (!row) return "Shipping Charge:";

  const name = str(row.shipping_name);
  const t = str(row.shipping_type ?? row.shippingType);
  const typeUp = t.toUpperCase();
  const pickup = isTruthyPickup(row.is_customer_pickup);
  const rawId = row.shipping__id ?? row.shipping_id;

  const treatAsUps =
    typeUp === "UPS" ||
    opts?.isUps === true ||
    looksLikeUpsServiceId(rawId);

  let inner: string | null = null;
  if (pickup) {
    const pickupLabel = name || "Customer pickup";
    if (treatAsUps) {
      const carrier = typeUp === "UPS" ? "UPS" : t || "UPS";
      inner = `${carrier}: ${pickupLabel}`;
    } else {
      inner = pickupLabel;
    }
  } else if (treatAsUps && name) {
    const carrier = typeUp === "UPS" ? "UPS" : t || "UPS";
    inner = `${carrier}: ${name}`;
  } else if (treatAsUps) {
    inner = "UPS";
  } else if (name) {
    inner = name;
  } else if (t) {
    inner = t;
  }

  if (!inner) return "Shipping Charge:";
  return `Shipping Charge: (${inner})`;
}

/**
 * Full left-column label for special fees, e.g. `Special Fee: (Speed Delivery)` or
 * `Special Fee: (Speed Delivery) +2 more`.
 */
export function formatSpecialFeeOrderTotalsLabel(fees: unknown[] | null | undefined): string {
  const arr = Array.isArray(fees) ? fees : [];
  const first = str((arr[0] as Record<string, unknown> | undefined)?.specialfee_name);
  const alt = first || str((arr[0] as Record<string, unknown> | undefined)?.name);
  if (!alt) return "Special Fee:";
  const extra = arr.length - 1;
  if (extra <= 0) return `Special Fee: (${alt})`;
  return `Special Fee: (${alt}) +${extra} more`;
}
